import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FileText, Users, Shield, AlertTriangle, Copyright, Calendar } from 'lucide-react';
import { getCanonicalUrl } from '../utils/seoUtils';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 py-12">
      <Helmet>
        <title>Terms of Service - Clock Online</title>
        <meta name="description" content="Clock Online Terms of Service. Please read these terms carefully before using our online clock and alarm services. Understand your rights and responsibilities." />
        <link rel="canonical" href={getCanonicalUrl('/terms-of-service')} />
        <meta property="og:title" content="Terms of Service - Clock Online" />
        <meta property="og:description" content="Clock Online Terms of Service. Please read these terms carefully before using our online clock and alarm services." />
        <meta property="og:url" content={getCanonicalUrl('/terms-of-service')} />
        <meta name="twitter:title" content="Terms of Service - Clock Online" />
        <meta name="twitter:description" content="Clock Online Terms of Service. Please read these terms carefully before using our services." />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-blue-600/30 p-8 shadow-2xl">
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <FileText className="w-8 h-8 text-cyan-400" />
              <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
            </div>
            <p className="text-white/70 text-lg">
              Please read these terms carefully before using Clock Online services.
            </p>
            <p className="text-white/50 text-sm mt-2">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          <div className="space-y-8">
            
            {/* Acceptance */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Acceptance of Terms</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  By accessing and using Clock Online, you accept and agree to be bound by the terms and provision of this agreement.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>These terms apply to all visitors, users, and others who access or use the service</li>
                  <li>If you disagree with any part of these terms, you may not access the service</li>
                  <li>Your continued use of the service constitutes acceptance of these terms</li>
                </ul>
              </div>
            </section>

            {/* Service Description */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Service Description</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  Clock Online provides online clock, alarm, timer, and related time management services:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Digital clock display with multiple time zones</li>
                  <li>Alarm clock functionality with various sound options</li>
                  <li>Timer and stopwatch features</li>
                  <li>Meditation timer with ambient sounds</li>
                  <li>Radio and video alarm capabilities</li>
                  <li>All services are provided "as is" without warranties</li>
                </ul>
              </div>
            </section>

            {/* User Responsibilities */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">User Responsibilities</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  As a user of Clock Online, you agree to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use the service only for lawful purposes</li>
                  <li>Not attempt to interfere with the service's operation</li>
                  <li>Not use the service to distribute malware or harmful content</li>
                  <li>Respect the intellectual property rights of the service</li>
                  <li>Not attempt to reverse engineer or copy the service</li>
                </ul>
              </div>
            </section>

            {/* Service Availability */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Service Availability</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  While we strive to provide reliable service, please note:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Service availability is not guaranteed 24/7</li>
                  <li>Maintenance and updates may cause temporary interruptions</li>
                  <li>We are not responsible for missed alarms due to service interruptions</li>
                  <li><strong>Important:</strong> Do not rely solely on Clock Online for critical time-sensitive events</li>
                  <li>Always have backup alarm methods for important appointments</li>
                  <li>Internet connectivity issues may affect service functionality</li>
                </ul>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Copyright className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Intellectual Property</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  The service and its original content, features, and functionality are and will remain the exclusive property of Clock Online:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>All content, including text, graphics, logos, and software, is protected by copyright</li>
                  <li>You may not reproduce, distribute, or create derivative works without permission</li>
                  <li>Third-party content may be subject to separate licensing terms</li>
                  <li>User-generated settings and preferences remain your property</li>
                </ul>
              </div>
            </section>

            {/* Disclaimers */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Disclaimers and Limitations</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  Clock Online is provided on an "as is" and "as available" basis:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We make no warranties, expressed or implied, regarding the service</li>
                  <li>We do not guarantee accuracy of time display or alarm functionality</li>
                  <li>We are not liable for any damages arising from use of the service</li>
                  <li>Users assume all risks associated with using the service</li>
                  <li>Our liability is limited to the maximum extent permitted by law</li>
                </ul>
              </div>
            </section>

            {/* Terms Modification */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Modification of Terms</h2>
              </div>
              <div className="text-white/80 space-y-3">
                <p>
                  We reserve the right to modify these terms at any time:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Changes will be posted on this page with an updated date</li>
                  <li>Significant changes may be highlighted on our website</li>
                  <li>Continued use after changes constitutes acceptance of new terms</li>
                  <li>We recommend reviewing these terms periodically</li>
                  <li>If you disagree with changes, discontinue use of the service</li>
                </ul>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-blue-600/30">
            <div className="text-center">
              <p className="text-white/60 text-sm">
                These Terms of Service are effective as of the date listed above and govern your use of Clock Online.
              </p>
              <p className="text-white/50 text-xs mt-2">
                For questions about these terms, contact us at <a href="mailto:legal@clockonline.com" className="text-cyan-400 hover:text-cyan-300">legal@clockonline.com</a>
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}